// ArtAdPreview.js
//
// 트랙 광고판 미디어아트 미리보기 — 러닝앱 유니티 소스를 그대로 옮긴 것이다.
//   배경 = Assets/Resources/Shaders/TrackMediaArt.shader 의 다섯 테마
//   글자 = Assets/Scripts/Race/TrackMediaArtRing.cs 의 AnimateLabel (테마별 등장·상시 연출)
// 색·타이밍의 정본은 유니티다. 여기 값을 바꾸면 «만든 것»과 «걸리는 것»이 달라진다.
//
// 유니티 ↔ 여기 대응
//   Petal  : 꽃잎 3층 낙하·회전, 크림 워시 / 글자는 가운데서 양옆으로 벌어지며(EaseOutBack) 알파 인
//   Sky    : 구름 2층 한 방향 스크롤 / 글자는 위에서 내려앉으며(살짝 크게→제자리) 알파 인
//   Sunset : 살구→분홍→보라 띠가 좌우로 흐름 / 글자는 왼쪽에서 밀려 들어옴(EaseOutBack, 글자순 시차)
//   Aurora : 딥틸 위 커튼 3장 / 글자는 고정, 0.5초 알파 인 + 민트 언더레이
//   Comet  : 검정 위를 혜성이 가로지름(7초 주기) / 혜성이 닿는 글자부터 흰빛·확대·살짝 위로
//   글자 위상은 «순번»이 아니라 **실제 x 좌표**에서 역산한다(유니티 CharacterX 와 같다).
import React, { useLayoutEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ── 테마 토큰 (셰이더·FaceTop/FaceBottom 에서 옮김) ───────────────────────────
//   ink*: TrackMediaArtRing.FaceBottom → FaceTop 세로 그라디언트.
export const ART_THEMES = [
  { key: 'petal',  name: '꽃잎',   desc: '크림 바탕에 꽃잎이 지고 글자가 피어난다',
    chipBg: 'linear-gradient(155deg,#FEF9F4,#F7E1E8)', chipInk: '#3B1326',
    inkTop: '#3B1326', inkBottom: '#85294F' },
  { key: 'sky',    name: '하늘',   desc: '구름이 흐르고 글자가 내려앉는다',
    chipBg: 'linear-gradient(180deg,#63BDE8,#D6EFFC)', chipInk: '#06213D',
    inkTop: '#06213D', inkBottom: '#0F577D' },
  { key: 'sunset', name: '노을',   desc: '살구→분홍→보라 색이 흐르고 글자가 실려 온다',
    chipBg: 'linear-gradient(96deg,#FFD192,#F57899,#966EC9)', chipInk: '#40132E',
    inkTop: '#40132E', inkBottom: '#732445' },
  { key: 'aurora', name: '오로라', desc: '딥틸 위로 빛 커튼이 지나가며 글자를 비춘다',
    chipBg: 'linear-gradient(90deg,#06141A,#0B3D33 50%,#2A1F5E)', chipInk: '#9BE9CE',
    inkTop: '#FAFFFF', inkBottom: '#63E0C9' },
  { key: 'comet',  name: '혜성',   desc: '검정 위를 혜성이 지나며 글자에 불을 붙인다',
    chipBg: '#040608', chipInk: '#00FFA3',
    inkTop: '#A8FFDB', inkBottom: '#00D475' },
];
export const themeOf = (key) => ART_THEMES.find(t => t.key === key) || ART_THEMES[0];

// 유니티 실기 사이클은 20초(등장 ~1.3초 → 유지 → 0.8초 크로스페이드). 미리보기는 9초로 압축한다.
const LOOP = 9;            // s
const COMET_PERIOD = 7;    // s — 유니티 CometDistance 의 7초 주기와 같다
const COMET_TRAVEL = 1.28; // 판 폭 대비 혜성 머리 이동 거리(0 → 1.28): 꼬리까지 빠져나가게

// ── 등장 키프레임 (AnimateLabel 을 9초 루프로) ──────────────────────────────
//   퍼센트 = 초/9. 등장은 각 글자의 지연(--d)으로 시차를 두고, 유지 구간엔 절대 사라지지 않는다.
const easeBack = 'cubic-bezier(.34,1.56,.64,1)';   // EaseOutBack

const petalIn = keyframes`
  0%       { opacity:0; transform: translateX(var(--dx)) scale(.94); }
  7%       { opacity:1; }
  13%, 90% { opacity:1; transform: translateX(0) scale(1); }
  100%     { opacity:0; transform: translateX(calc(var(--dx) * .4)) scale(1); }
`;
const skyIn = keyframes`
  0%       { opacity:0; transform: translateY(-18%) scale(1.035); filter: blur(3px); }
  8%       { opacity:1; }
  14%, 90% { opacity:1; transform: none; filter: blur(0); }
  100%     { opacity:0; transform: translateY(6%); filter: blur(2px); }
`;
const sunsetIn = keyframes`
  0%       { opacity:0; transform: translateX(calc(var(--h) * -.55)); }
  6%       { opacity:1; }
  11%, 90% { opacity:1; transform: none; }
  100%     { opacity:0; transform: translateX(calc(var(--h) * .08)); }
`;
const auroraIn = keyframes`
  0%      { opacity:0; }
  6%, 90% { opacity:1; }
  100%    { opacity:0; }
`;
const cometIn = keyframes`
  from { opacity:0; } to { opacity:1; }
`;
// 혜성이 닿는 순간이 0% — animation-delay 가 곧 «혜성이 이 글자에 닿는 시각».
const cometLight = keyframes`
  0%        { color:#F5FFFC; transform: scale(1.09) translateY(-4%);
              filter: drop-shadow(0 0 10px rgba(0,255,163,.95)) drop-shadow(0 0 22px rgba(0,255,163,.55)); }
  9%        { color:#DFFFF2; transform: scale(1.03) translateY(-1%);
              filter: drop-shadow(0 0 8px rgba(0,255,163,.5)); }
  26%, 100% { color:transparent; transform: none; filter: drop-shadow(0 0 3px rgba(0,140,80,.35)); }
`;

// 상시(hold) 미세 움직임 — 유니티 settled 항 (Petal 흔들림·Sky 부유·Sunset 드리프트)
const petalHold  = keyframes` from { transform: translateY(0) rotate(-.5deg); } to { transform: translateY(2%) rotate(.5deg); } `;
const skyHold    = keyframes` from { transform: translate(-1%,-2%); } to { transform: translate(1%,2%); } `;
const sunsetHold = keyframes` from { transform: translateX(-2%) scale(.99); } to { transform: translateX(2%) scale(1.01); } `;

// ── 배경 키프레임 (TrackMediaArt.shader) ────────────────────────────────────
const petalFall = keyframes`
  0%   { top:-30%; transform: translateX(0)    rotate(0deg);   opacity:0; }
  7%   { opacity:1; }
  22%  { transform: translateX(30px)  rotate(150deg); }
  45%  { transform: translateX(-18px) rotate(300deg); }
  68%  { transform: translateX(38px)  rotate(455deg); }
  88%  { transform: translateX(6px)   rotate(560deg); opacity:1; }
  100% { top:125%; transform: translateX(22px) rotate(640deg); opacity:0; }
`;
const petalWash = keyframes` from { transform: translateX(-5%) scale(1); } to { transform: translateX(5%) scale(1.1); } `;
const scrollA   = keyframes` from { background-position: 0 0; } to { background-position: -620px 0; } `;
const scrollB   = keyframes` from { background-position: 0 0; } to { background-position: -980px 0; } `;
const sunFlow   = keyframes` from { background-position: 0% 0; } to { background-position: 100% 0; } `;
const curA = keyframes` from { transform: translateX(0)    skewX(-16deg) scaleY(.85); } to { transform: translateX(150%) skewX(-4deg)  scaleY(1.1); } `;
const curB = keyframes` from { transform: translateX(60%)  skewX(-6deg)  scaleY(1.1); } to { transform: translateX(-40%) skewX(-18deg) scaleY(.8); } `;
const curC = keyframes` from { transform: translateX(-30%) skewX(-12deg) scaleY(.9); } to { transform: translateX(110%) skewX(-2deg)  scaleY(1.15); } `;
const cometRun = keyframes` from { left: 0%; } to { left: ${COMET_TRAVEL * 100}%; } `;

// ── 판 ───────────────────────────────────────────────────────────────────
const Board = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 500 / 93.5;          /* 유니티 보드 캔버스 비율 5.35:1 */
  min-height: 72px;
  max-height: 110px;
  border-radius: 6px;
  overflow: hidden;
  isolation: isolate;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.10);
  --h: 100px;

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation: none !important; }
  }
`;

const Layer = styled.div`
  position: absolute; inset: 0; pointer-events: none;
`;

// T1 꽃잎 — 셰이더 petal(): 크림 바탕 + 3층 꽃잎(뒤 작고 흐리게, 앞 크고 또렷하게)
const PetalBg = styled(Layer)`
  background: linear-gradient(155deg,#FEF9F4 0%,#F7ECEA 46%,#F3E0E4 100%);
`;
const PetalWash = styled(Layer)`
  inset: -20%;
  background:
    radial-gradient(42% 130% at 18% 30%, rgba(255,206,222,.62), transparent 68%),
    radial-gradient(38% 120% at 62% 78%, rgba(255,228,196,.58), transparent 66%),
    radial-gradient(34% 110% at 88% 26%, rgba(222,206,255,.52), transparent 66%);
  animation: ${petalWash} 17s ease-in-out infinite alternate;
`;
const PETAL_INK = ['linear-gradient(140deg,#FFD3E0,#FA78A3)', 'linear-gradient(140deg,#EEDDFF,#AD82DE)',
                   'linear-gradient(140deg,#FFE9CC,#FFBA7A)', 'linear-gradient(140deg,#FFFFFF,#FFCDDD)',
                   'linear-gradient(140deg,#FFC9C2,#EC7F85)'];
const Petal = styled.i`
  position: absolute; top: -30%; display: block;
  border-radius: 64% 10% 64% 10%;
  animation: ${petalFall} linear infinite;
`;
// 결정적 의사난수 — 렌더마다 꽃잎이 튀지 않게
const rnd = (i, k) => { const x = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return x - Math.floor(x); };
const PETALS = Array.from({ length: 30 }, (_, i) => {
  const layer = i % 3;                            // 0 뒤 · 1 중간 · 2 앞
  const w = [11, 17, 24][layer], h = [8, 12, 18][layer];
  return {
    left: `${Math.floor(rnd(i, 1) * 100)}%`,
    w, h, layer,
    ink: PETAL_INK[Math.floor(rnd(i, 2) * PETAL_INK.length)],
    dur: `${(9.5 - layer * 2 + rnd(i, 3) * 2.5).toFixed(2)}s`,
    delay: `-${(rnd(i, 4) * 9).toFixed(2)}s`,
  };
});

// T2 하늘 — 셰이더 sky(): 아래 밝고 위 진한 하늘 + 구름 2층 한 방향 스크롤(시차)
const SkyBg = styled(Layer)`
  background: linear-gradient(180deg,#63BDE8 0%,#A6DAF4 55%,#D6EFFC 100%);
`;
const CloudFront = styled(Layer)`
  filter: blur(2px);
  background-repeat: repeat-x; background-size: 620px 100%;
  background-image:
    radial-gradient(46px 26px at 60px 62%,  #fff 52%, transparent 80%),
    radial-gradient(68px 38px at 112px 55%, #fff 52%, transparent 80%),
    radial-gradient(52px 29px at 164px 62%, #fff 52%, transparent 80%),
    radial-gradient(34px 19px at 198px 67%, #fff 52%, transparent 80%),
    radial-gradient(40px 23px at 392px 31%, rgba(255,255,255,.95) 52%, transparent 80%),
    radial-gradient(58px 32px at 438px 25%, #fff 52%, transparent 80%),
    radial-gradient(42px 24px at 484px 31%, rgba(255,255,255,.95) 52%, transparent 80%);
  animation: ${scrollA} 17s linear infinite;
`;
const CloudBack = styled(Layer)`
  filter: blur(8px); opacity: .55;
  background-repeat: repeat-x; background-size: 980px 100%;
  background-image:
    radial-gradient(84px 44px at 150px 72%,  rgba(255,255,255,.85) 52%, transparent 80%),
    radial-gradient(118px 60px at 232px 64%, rgba(255,255,255,.90) 52%, transparent 80%),
    radial-gradient(90px 46px at 318px 72%,  rgba(255,255,255,.80) 52%, transparent 80%),
    radial-gradient(72px 38px at 620px 26%,  rgba(255,255,255,.75) 52%, transparent 80%),
    radial-gradient(100px 52px at 700px 20%, rgba(255,255,255,.85) 52%, transparent 80%),
    radial-gradient(76px 40px at 776px 26%,  rgba(255,255,255,.75) 52%, transparent 80%);
  animation: ${scrollB} 31s linear infinite;
`;

// T3 노을 — 셰이더 sunset(): 살구→분홍→보라 띠가 천천히 흐르고 아래쪽에 옅은 노란 헤이즈
const SunsetBg = styled(Layer)`
  background: linear-gradient(96deg,#FFD192 0%,#FFB07E 22%,#F57899 50%,#B96BC9 76%,#966EC9 100%);
  background-size: 220% 100%;
  animation: ${sunFlow} 24s ease-in-out infinite alternate;
`;
const SunsetHaze = styled(Layer)`
  background: radial-gradient(64% 190% at 74% 120%, rgba(255,230,190,.55), transparent 62%);
`;

// T4 오로라 — 셰이더 aurora(): 딥틸 + 민트·시안·보라 커튼.
//   ★글자는 다른 테마와 똑같이 가운데다(유니티는 테마 무관하게 호 중앙). 목업의 «왼쪽 스크림» 배치는 쓰지 않고,
//     커튼 위에서 흰 글자가 묻히지 않게 글자 뒤 가운데만 살짝 어둡게 깐다.
const AuroraBg = styled(Layer)` background: #06141A; `;
const Curtain = styled(Layer)`
  top: -35%; bottom: -35%; left: ${p => p.left}; width: 26%;
  filter: blur(11px); mix-blend-mode: screen;
  background: linear-gradient(180deg, transparent 4%, ${p => p.color} 48%, transparent 96%);
  animation: ${p => p.anim} ${p => p.dur} ease-in-out infinite alternate;
`;
const Scrim = styled(Layer)`
  background: radial-gradient(60% 120% at 50% 50%, rgba(3,12,15,.62), rgba(3,12,15,.30) 60%, rgba(3,12,15,0) 100%);
`;

// T5 혜성 — 셰이더 comet(): 꼬리+머리가 한 덩어리로 7초에 판을 가로지른다
const CometBg = styled(Layer)` background: #040608; `;
const Comet = styled.div`
  position: absolute; top: 50%; left: 0; width: 0; height: 0;
  animation: ${cometRun} ${COMET_PERIOD}s linear infinite;
  pointer-events: none;
`;
const CometTail = styled.i`
  position: absolute; right: 0; top: -1px; height: 2px; width: 140px; display: block; border-radius: 2px;
  background: linear-gradient(90deg, transparent, rgba(5,158,99,.25) 40%, rgba(5,158,99,.9));
`;
const CometHead = styled.i`
  position: absolute; right: -11px; top: -11px; width: 22px; height: 22px; display: block; border-radius: 50%;
  background: radial-gradient(closest-side, #FFFFFF, rgba(0,255,163,.7) 40%, transparent 72%);
`;

// ── 글자 ─────────────────────────────────────────────────────────────────
//   글자 높이 = 판 높이의 60% (유니티 label.Scale). 색은 아래→위 그라디언트, 언더레이는 drop-shadow.
// ★세로 중앙은 flex 로 잡는다. translateY(-50%) 로 잡으면 아래 hold 애니메이션의 transform 이
//   그걸 덮어써서 글자가 판 아래로 내려앉는다(실측).
const Line = styled.div`
  position: absolute; left: 4.5%; right: 4.5%; top: 0; bottom: 0;
  display: flex; align-items: center;
  justify-content: ${p => p.center ? 'center' : 'flex-start'};
  white-space: nowrap; overflow: hidden;
  font-size: calc(var(--h) * .52);
  font-weight: 900; letter-spacing: -.03em; line-height: 1;
  ${p => p.hold && css`animation: ${p.hold} 6.6s ease-in-out infinite alternate;`}
`;
const Char = styled.span`
  display: inline-block; will-change: transform, opacity;
  background: linear-gradient(0deg, ${p => p.inkBottom}, ${p => p.inkTop});
  -webkit-background-clip: text; background-clip: text;
  color: transparent;
  --dx: 0px; --d: 0s;
  animation-fill-mode: both;
  ${p => p.theme === 'petal' && css`
    filter: drop-shadow(1px 2px 1px rgba(48,9,33,.28));
    animation: ${petalIn} ${LOOP}s ${easeBack} var(--d) infinite;`}
  ${p => p.theme === 'sky' && css`
    filter: drop-shadow(1px 2px 1px rgba(48,9,33,.22));
    animation: ${skyIn} ${LOOP}s cubic-bezier(.25,.9,.3,1) var(--d) infinite;`}
  ${p => p.theme === 'sunset' && css`
    filter: drop-shadow(1px 2px 1px rgba(48,9,33,.28));
    animation: ${sunsetIn} ${LOOP}s ${easeBack} var(--d) infinite;`}
  ${p => p.theme === 'aurora' && css`
    filter: drop-shadow(0 0 6px rgba(0,204,135,.55)) drop-shadow(0 0 14px rgba(0,204,135,.25));
    animation: ${auroraIn} ${LOOP}s ease-out infinite;`}
  ${p => p.theme === 'comet' && css`
    filter: drop-shadow(0 0 3px rgba(0,140,80,.35));
    animation: ${cometIn} .5s ease-out 1 both, ${cometLight} ${COMET_PERIOD}s linear var(--d) infinite;`}
`;

// 글자별 위상 — «순번 등분»이 아니라 실제 x 에서. 한글·영문·공백 폭이 제각각이라 순번으로 나누면
// 혜성이 이미 지나간 글자가 뒤늦게 켜진다(유니티 CharacterX 와 같은 이유).
const useCharPhase = (text, themeKey) => {
  const lineRef = useRef(null);
  useLayoutEffect(() => {
    const line = lineRef.current; if (!line) return;
    const board = line.parentElement; if (!board) return;
    const bw = board.clientWidth || 1;
    const bh = board.clientHeight || 100;
    board.style.setProperty('--h', `${bh}px`);
    const spans = line.querySelectorAll('span');
    // ★레이아웃 좌표(offsetLeft)로 잰다. getBoundingClientRect 는 진행 중인 등장 transform 이 섞여
    //   문구를 바꿀 때마다 위상이 틀어진다.
    const lineLeft = line.offsetLeft;
    let minX = Infinity, maxX = -Infinity;
    const centers = [];
    spans.forEach(sp => {
      const l = lineLeft + sp.offsetLeft, r = l + sp.offsetWidth;
      centers.push((l + r) / 2);                         // 판 기준 px
      minX = Math.min(minX, l); maxX = Math.max(maxX, r);
    });
    const width = Math.max(1, maxX - minX);
    const mid = (minX + maxX) / 2;
    spans.forEach((sp, i) => {
      const x = centers[i] - mid;                  // 중앙에서의 거리(px) = 유니티 CharacterX
      const across = (centers[i] - minX) / width;  // 0..1
      let delay = 0, dx = 0;
      switch (themeKey) {
        case 'petal':  delay = Math.abs(across - .5) * .38; dx = -x * .20; break;   // 가운데부터 벌어짐
        case 'sky':    delay = across * .22; break;                                  // 왼쪽부터 내려앉음
        case 'sunset': delay = across * .26; break;                                  // 흐르는 방향으로
        case 'comet':  delay = (centers[i] / bw) / COMET_TRAVEL * COMET_PERIOD; break; // 혜성이 닿는 시각
        default: break;
      }
      sp.style.setProperty('--d', `${delay.toFixed(3)}s`);
      sp.style.setProperty('--dx', `${dx.toFixed(1)}px`);
    });
  }, [text, themeKey]);
  return lineRef;
};

const ArtAdPreview = ({ themeKey, text }) => {
  const theme = themeOf(themeKey);
  const copy = (text || '').trim() || ' ';
  const chars = Array.from(copy);
  const lineRef = useCharPhase(copy, theme.key);
  const hold = { petal: petalHold, sky: skyHold, sunset: sunsetHold }[theme.key];
  const center = true;   // 유니티는 테마 무관하게 가운데

  return (
    <Board title={theme.desc}>
      {theme.key === 'petal' && (
        <>
          <PetalBg /><PetalWash />
          {PETALS.map((p, i) => (
            <Petal key={i} style={{
              left: p.left, width: p.w, height: p.h,
              background: p.ink,
              opacity: [.6, .9, 1][p.layer],
              filter: p.layer === 0 ? 'blur(1.2px)' : 'none',
              boxShadow: p.layer === 2 ? '0 2px 5px rgba(170,110,135,.22)' : 'none',
              animationDuration: p.dur, animationDelay: p.delay,
              zIndex: p.layer === 2 ? 3 : 0,
            }} />
          ))}
        </>
      )}
      {theme.key === 'sky' && (<><SkyBg /><CloudBack /><CloudFront /></>)}
      {theme.key === 'sunset' && (<><SunsetBg /><SunsetHaze /></>)}
      {theme.key === 'aurora' && (
        <>
          <AuroraBg />
          <Curtain left="-24%" color="rgba(0,255,163,.95)"  anim={curA} dur="9s" />
          <Curtain left="16%"  color="rgba(56,214,235,.85)" anim={curB} dur="12.5s" />
          <Curtain left="54%"  color="rgba(150,110,255,.8)" anim={curC} dur="16s" />
          <Scrim />
        </>
      )}
      {theme.key === 'comet' && (
        <>
          <CometBg />
          <Comet><CometTail /><CometHead /></Comet>
        </>
      )}
      <Line ref={lineRef} center={center} hold={hold} style={{ zIndex: 2 }}>
        {chars.map((ch, i) => (
          <Char key={i} theme={theme.key} inkTop={theme.inkTop} inkBottom={theme.inkBottom}>
            {ch === ' ' ? ' ' : ch}
          </Char>
        ))}
      </Line>
    </Board>
  );
};

export default ArtAdPreview;
